// The New-Front onboarding order is load-bearing: WhoBrand → WorkspaceSettings →
// ConnectPlatforms → OneLastThing. Legacy route names remain accepted as aliases.

/** The wizard's route name — the guard, the topbar CTA and the Shopify resolver all key on it. */
export const ONBOARDING_ROUTE_NAME = 'Onboarding'

export type OnboardingStepKey =
  | 'who-brand'
  | 'found-brand'
  | 'business-type'
  | 'connect'
  | 'one-last-thing'
  | 'workspace'
  | 'settings'
  | 'authentications'
  | 'connections'

/** Tuple, not `readonly OnboardingStepKey[]`: the first entry must stay literal-typed so the
 *  `stepAt`/route fallbacks are total under `noUncheckedIndexedAccess`. */
export const ONBOARDING_STEPS = [
  'who-brand',
  'settings',
  'connections',
  'one-last-thing',
] as const satisfies readonly OnboardingStepKey[]

export const BUSINESS_TYPE_OPTIONS = ['ecom', 'lead_gen', 'subscription_app'] as const
export type BusinessType = (typeof BUSINESS_TYPE_OPTIONS)[number]

export const HEARD_ABOUT_OPTIONS = [
  'Google or another search engine',
  'ChatGPT, Claude or another AI assistant',
  'Someone I know (friend, colleague or agency)',
  'Social media, newsletter or podcast',
  'Something else',
] as const
export type HeardAboutSource = (typeof HEARD_ABOUT_OPTIONS)[number]

export const FIRST_ONBOARDING_STEP: OnboardingStepKey = ONBOARDING_STEPS[0]

/** Step 1 request — `POST /accounts/{accountId}/workspace/create/` (§6.1). */
export interface CreateWorkspacePayload {
  name: string
  account_id: number
  mcp_only?: boolean
}

export interface CreateWorkspaceResponse {
  workspace: number
}

/** Step 1's second call; seeds step 2 — `POST …/workspace/brand-context/profile/` (§6.2). */
export interface BrandContextRequest {
  domain: string
  /** `'UTC+3'` — derived from the browser, legacy shape preserved verbatim. */
  timezone: string
  language: string
  workspace_name: string
}

/**
 * The brand-context response (§5, BE-29-04 — reverse-engineered from the legacy READ sites).
 * The asymmetry is load-bearing: `branded_company`/`branded_competitors` are `{ value }[]` while
 * `branded_slug` is a single `{ value }`.
 */
export interface WorkspaceBrandDefaults {
  /** v3 read it as the business-type fallback (`OnboardingWorkspaceSettings.vue:254`). */
  business_type?: { value: string }
  timezone?: { value: string }
  currency?: { value: string }
  language?: { value: string }
  key_metric_preference?: { value: 'conversion_count' | 'conversion_value' }
  branded_company?: { value: string }[]
  branded_competitors?: { value: string }[]
  branded_slug?: { value: string }
}

export interface OnboardingLiveAdPreview {
  id: string
  name: string
  imageUrl: string | null
}

export interface OnboardingLiveAds {
  count: number
  previews: OnboardingLiveAdPreview[]
}

/**
 * Step 2's form model — the typed replacement for the legacy untyped `steps` array.
 *
 * Two of the contract's seven fields are deliberately absent, both for the same reason: their
 * catalogs are not reachable from this module and writing an unvalidated value would corrupt an
 * attribute another layer reads.
 *   • `language` — `shared/config/languages.ts` (§3.5) has not landed; the only catalog is 22's
 *     `LANGUAGE_OPTIONS`, whose stored value is a `{language_name, language_code, criterion_id}`
 *     row, not a string.
 *   • `currency` — the stored value is 22's `{code, label}` OBJECT, and 07 derives
 *     `useWorkspace().currency` from `value.code`; `shared/config/currencies.ts` carries `{code}`
 *     only, so writing it from here would un-format currency across every report.
 * Both remain 22's Basics pane, which owns those catalogs.
 */
export type OnboardingFieldId =
  'timezone' | 'key_metric_preference' | 'branded_company' | 'branded_competitors' | 'branded_slug'

/** Which transport the field is written through (§6.4). */
export type OnboardingFieldTransport = 'setting' | 'attribute'

export interface OnboardingFieldSpec {
  id: OnboardingFieldId
  kind: 'select' | 'keywords'
  required: boolean
  transport: OnboardingFieldTransport
  /** The workspace-attribute key the value is written under. `branded_*` share one key (§6.4). */
  attributeKey: 'key_metric_preference' | 'branded_keywords' | null
}

/** A resolved field: its spec plus the value the brand-context/attribute lookup produced. */
export interface OnboardingField {
  id: OnboardingFieldId
  kind: 'select' | 'keywords'
  required: boolean
  transport: OnboardingFieldTransport
  attributeKey: OnboardingFieldSpec['attributeKey']
  /** `select` → a string; `keywords` → a string list. `null`/`[]` mean "not answered". */
  value: string | string[] | null
}

export const KEY_METRIC_OPTIONS = ['conversion_count', 'conversion_value'] as const
export type KeyMetricPreference = (typeof KEY_METRIC_OPTIONS)[number]

/** What the wizard writes when step 2 is applied. */
export interface OnboardingDefaultsPayload {
  /** `POST …/settings/update/` body — only ever `{ timezone }` today (§6.4). */
  settings: Record<string, string>
  /** One entry per attribute key; `branded_*` are already coalesced into `branded_keywords`. */
  attributes: { key: string; value: unknown }[]
}
